/**
 * Visual change applier (contract §3.3).
 *
 * CSS-first: `style`, `hide` and non-<img> `image` changes become rules in
 * ONE managed stylesheet (adoptedStyleSheets when available, otherwise a
 * <style data-b2m-visual> element). They take effect before the element
 * even exists, survive framework re-renders and need no inline styles.
 *
 * DOM part: `text`, `attribute`, <img> `image` and `insert` (sanitized HTML
 * placed next to / inside the element, or in its place: `replace` hides the
 * element through the managed stylesheet and puts the HTML right after it)
 * changes are written to the matched elements. An insert may carry scoped
 * `css`, sanitized and kept in the managed stylesheet while it is applied. Each element gets a MutationObserver with a narrow filter
 * that re-applies the desired value when the framework overwrites it, with a
 * "is the desired value already set?" check so our own writes never loop.
 * A global rAF-batched observer resolves selectors that match late and
 * elements that get replaced.
 *
 * Circuit breaker: a change re-applied more than MAX_REAPPLIES_PER_SECOND
 * times is disabled and reported once (`ve_change_conflict`). A selector
 * that never matches within `waitForElementMs` is reported once per page
 * (`ve_change_missing`). Every change runs in its own try/catch.
 *
 * Standalone module (no imports): the editor bundle and the unit tests load
 * it directly.
 */

export const STYLE_ATTR = 'data-b2m-visual';
export const MAX_REAPPLIES_PER_SECOND = 20;
export const STYLE_ALLOWLIST = [
  'color', 'background-color', 'background', 'background-image',
  'font-size', 'font-weight', 'font-family', 'font-style',
  'text-align', 'text-decoration', 'text-transform', 'line-height', 'letter-spacing',
  'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'border', 'border-top', 'border-right', 'border-bottom', 'border-left',
  'border-color', 'border-width', 'border-style', 'border-radius',
  'width', 'height', 'max-width', 'min-width', 'max-height', 'min-height',
  'opacity', 'display', 'visibility', 'box-shadow', 'cursor'
];
export const ATTR_ALLOWLIST = ['href', 'alt', 'title', 'target', 'aria-label', 'placeholder'];

const CSS_ACTIONS = { style: true, hide: true };

// ── insert: sanitized HTML ────────────────────────────────────────────────
export const INSERTED_ATTR = 'data-b2m-ve-inserted';
export const REPLACED_ATTR = 'data-b2m-ve-replaced';
export const INSERT_POSITIONS = ['beforebegin', 'afterbegin', 'beforeend', 'afterend', 'replace'];
export const INSERT_MAX_LENGTH = 20000;
export const INSERT_CSS_MAX_LENGTH = 10000;
/** Any of these in an insert's css rejects the whole css (the HTML still applies). */
const CSS_REJECT = [/<\/\s*style/i, /@import/i, /expression\s*\(/i, /javascript\s*:/i, /behavior\s*:/i, /-moz-binding/i];
/** Elements kept (with allowed attributes); anything else is unwrapped. */
export const HTML_TAG_ALLOWLIST = [
  'a', 'abbr', 'b', 'blockquote', 'br', 'button', 'code', 'dd', 'del', 'div', 'dl', 'dt', 'em',
  'figcaption', 'figure', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img', 'ins', 'label',
  'li', 'mark', 'ol', 'p', 'picture', 'pre', 's', 'section', 'small', 'source', 'span', 'strong',
  'sub', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'u', 'ul'
];
/** Elements dropped together with their content. */
const HTML_DROP_TAGS = [
  'script', 'style', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet', 'link', 'meta',
  'base', 'form', 'input', 'select', 'textarea', 'option', 'svg', 'math', 'template', 'noscript',
  'video', 'audio', 'canvas', 'portal'
];
const HTML_ATTR_ALLOWLIST = [
  'class', 'id', 'title', 'alt', 'src', 'srcset', 'sizes', 'href', 'target', 'rel', 'style',
  'type', 'width', 'height', 'loading', 'role', 'colspan', 'rowspan', 'name', 'value', 'disabled'
];
const URL_ATTRS = { src: true, href: true, srcset: true };

function safeUrl(value) {
  const v = String(value == null ? '' : value).trim();
  // Strip control chars/whitespace browsers ignore inside a scheme ("java\nscript:").
  const probe = v.replace(/[\u0000-\u0020\u007f]/g, '').toLowerCase();
  if (/^(javascript|vbscript|data):/.test(probe)) {
    return /^data:image\/(png|gif|jpe?g|webp|avif);/.test(probe) ? v : '';
  }
  return v;
}

function safeInlineStyle(value) {
  const v = String(value == null ? '' : value);
  if (/expression\s*\(|javascript:|behavior\s*:|-moz-binding|@import/i.test(v)) return '';
  return v.replace(/[<>]/g, '');
}

/**
 * Parse `html` into an inert fragment and keep only allow-listed markup:
 * no scripts, no event handlers, no javascript: URLs, no forms or frames.
 * `<a target="_blank">` gets rel="noopener noreferrer". Returns null when
 * nothing is left.
 */
export function sanitizeHtml(doc, html) {
  const source = String(html == null ? '' : html).slice(0, INSERT_MAX_LENGTH);
  if (!source.trim() || !doc) return null;
  const tpl = doc.createElement('template');
  tpl.innerHTML = source; // template content is inert: no script runs, no image loads
  const clean = (parent) => {
    Array.from(parent.childNodes).forEach(node => {
      if (node.nodeType === 8) { parent.removeChild(node); return; } // comment
      if (node.nodeType !== 1) return;
      const tag = String(node.tagName).toLowerCase();
      if (HTML_DROP_TAGS.includes(tag)) { parent.removeChild(node); return; }
      clean(node);
      if (!HTML_TAG_ALLOWLIST.includes(tag)) {
        while (node.firstChild) parent.insertBefore(node.firstChild, node);
        parent.removeChild(node);
        return;
      }
      Array.from(node.attributes).forEach(attr => {
        const name = attr.name.toLowerCase();
        const keep = HTML_ATTR_ALLOWLIST.includes(name) || /^aria-[a-z-]+$/.test(name) || /^data-[a-z0-9-]+$/.test(name);
        if (!keep || name === INSERTED_ATTR) { node.removeAttribute(attr.name); return; }
        if (URL_ATTRS[name]) {
          const url = safeUrl(attr.value);
          if (!url) node.removeAttribute(attr.name); else if (url !== attr.value) node.setAttribute(attr.name, url);
        } else if (name === 'style') {
          const css = safeInlineStyle(attr.value);
          if (!css) node.removeAttribute(attr.name); else node.setAttribute('style', css);
        }
      });
      if (tag === 'a' && node.getAttribute('target') === '_blank') node.setAttribute('rel', 'noopener noreferrer');
      if (tag === 'button' && !node.hasAttribute('type')) node.setAttribute('type', 'button');
    });
  };
  clean(tpl.content);
  if (!tpl.content.childNodes.length) return null;
  return tpl.content;
}

/**
 * Validate an insert change's extra CSS. Returns the CSS unchanged, or ''
 * when it is empty, too long, contains a rejected token (also when hidden
 * behind comments or CSS escapes) or has unbalanced braces (which would
 * swallow the other rules of the managed stylesheet).
 */
export function sanitizeCss(css) {
  const source = String(css == null ? '' : css);
  if (!source.trim() || source.length > INSERT_CSS_MAX_LENGTH) return '';
  const decoded = source
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/\\([0-9a-f]{1,6})\s?/gi, (m, hex) => {
      const cp = parseInt(hex, 16);
      return cp > 0 && cp <= 0x10ffff ? String.fromCodePoint(cp) : '';
    })
    .replace(/\\(.)/g, '$1');
  if (CSS_REJECT.some(re => re.test(source) || re.test(decoded))) return '';
  let depth = 0;
  let quote = '';
  for (let i = 0; i < source.length; i++) {
    const ch = source[i];
    if (quote) {
      if (ch === '\\') i++;
      else if (ch === quote) quote = '';
      continue;
    }
    if (ch === '/' && source[i + 1] === '*') {
      const end = source.indexOf('*/', i + 2);
      if (end === -1) return '';
      i = end + 1;
    } else if (ch === '"' || ch === "'") quote = ch;
    else if (ch === '{') depth++;
    else if (ch === '}' && --depth < 0) return '';
  }
  return depth === 0 && !quote ? source : '';
}

/** Token naming one replace change in the host's REPLACED_ATTR list. */
function replaceToken(key) {
  return String(key).replace(/\s+/g, '_');
}

function replaceRule(key) {
  const token = replaceToken(key).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `[${REPLACED_ATTR}~="${token}"]{display:none !important}`;
}

function sanitizeCssValue(value) {
  let v = String(value == null ? '' : value);
  v = v.replace(/[;{}]/g, '').replace(/!\s*important/gi, '');
  if (/expression\s*\(|javascript:|behavior\s*:/i.test(v)) return '';
  return v.trim();
}

function sanitizeUrl(value) {
  const v = String(value == null ? '' : value).trim();
  if (!v || /^javascript:/i.test(v) || /^data:text\/html/i.test(v)) return '';
  return v.replace(/["'()\s]/g, '');
}

function styleRule(selector, props) {
  const decls = [];
  Object.keys(props || {}).forEach(k => {
    const prop = String(k).trim().toLowerCase();
    if (!STYLE_ALLOWLIST.includes(prop)) return;
    const val = sanitizeCssValue(props[k]);
    if (!val) return;
    decls.push(`${prop}:${val} !important`);
  });
  return decls.length ? `${selector}{${decls.join(';')}}` : '';
}

export default class VisualApplier {
  /**
   * @param {{ win?: Window, doc?: Document, logger?: object, track?: Function, nonce?: string, exclude?: Function }} [opts]
   */
  constructor(opts = {}) {
    this.win = opts.win || (typeof window !== 'undefined' ? window : null);
    this.doc = opts.doc || (this.win ? this.win.document : null);
    this.logger = opts.logger || { log() {}, warn() {}, error() {} };
    this.track = typeof opts.track === 'function' ? opts.track : () => {};
    this.nonce = opts.nonce || '';
    this.exclude = typeof opts.exclude === 'function' ? opts.exclude : null;
    this.now = typeof opts.now === 'function' ? opts.now : () => Date.now();

    this.cssRules = new Map();     // ruleKey → css text
    this.domChanges = new Map();   // ruleKey → record
    this.applied = new Map();      // experimentId → { groupId, appliedAt, keys[] }
    this.sheet = null;
    this.styleEl = null;
    this.globalObserver = null;
    this.flushScheduled = false;
    this.timers = new Set();
  }

  // ── public ────────────────────────────────────────────────────────────

  /**
   * Apply a variant's enabled changes for an experiment. Re-applying the
   * same group is a no-op; a different group reverts the previous one first.
   * @returns {{ applied: number, pending: number }}
   */
  apply(experimentId, variant, settings = {}, meta = {}) {
    const expId = String(experimentId);
    const groupId = variant && variant.groupId != null ? String(variant.groupId) : (meta.groupId || '');
    const prev = this.applied.get(expId);
    if (prev && prev.groupId === groupId) return { applied: prev.keys.length, pending: this._pendingCount(expId), alreadyApplied: true };
    if (prev) this.revert(expId);

    const changes = (variant && Array.isArray(variant.changes) ? variant.changes : []).filter(c => c && c.enabled !== false);
    const waitMs = Number(settings.waitForElementMs);
    const record = { groupId, appliedAt: this.now(), keys: [], waitForElementMs: Number.isFinite(waitMs) ? waitMs : 5000 };
    this.applied.set(expId, record);

    let applied = 0;
    changes.forEach(change => {
      const key = `${expId}::${change.id}`;
      try {
        if (!this._validSelector(change.selector)) {
          this.logger.warn('[B2M Visual] invalid selector skipped:', change.selector);
          return;
        }
        if (CSS_ACTIONS[change.action]) {
          const css = change.action === 'hide'
            ? `${change.selector}{display:none !important}`
            : styleRule(change.selector, change.value);
          if (css) { this.cssRules.set(key, css); record.keys.push(key); applied++; }
          return;
        }
        if (change.action === 'image' && !this._firstMatchIsImg(change.selector)) {
          const src = sanitizeUrl(change.value && change.value.src);
          if (src) {
            this.cssRules.set(key, `${change.selector}{background-image:url("${src}") !important}`);
            record.keys.push(key);
            applied++;
          }
          return;
        }
        const rec = {
          key, expId, change, targets: [], reapplies: [], tripped: false,
          missingReported: false, firstTriedAt: this.now(), waitForElementMs: record.waitForElementMs
        };
        if (change.action === 'insert') this._insertCss(key, change);
        this.domChanges.set(key, rec);
        record.keys.push(key);
        if (this._resolveTargets(rec) > 0) applied++;
      } catch (e) {
        this.logger.error('[B2M Visual] change failed:', change && change.id, e);
      }
    });
    this._renderCss();
    this._ensureGlobalObserver();
    this._scheduleMissingCheck(record.waitForElementMs);
    return { applied, pending: this._pendingCount(expId) };
  }

  /** Revert everything applied for one experiment. */
  revert(experimentId) {
    const expId = String(experimentId);
    const record = this.applied.get(expId);
    if (!record) return false;
    record.keys.forEach(key => {
      this.cssRules.delete(key);
      this.cssRules.delete(`${key}::css`);
      this.cssRules.delete(`${key}::replace`);
      const rec = this.domChanges.get(key);
      if (rec) {
        rec.targets.forEach(t => this._restoreTarget(rec, t));
        rec.targets = [];
        this.domChanges.delete(key);
      }
    });
    this.applied.delete(expId);
    this._renderCss();
    if (this.domChanges.size === 0) this._stopGlobalObserver();
    return true;
  }

  revertAll() {
    Array.from(this.applied.keys()).forEach(id => this.revert(id));
  }

  /** Force a re-resolution pass (debug / SPA): pending changes get a fresh wait window. */
  reapplyAll() {
    let maxWait = 0;
    this.domChanges.forEach(rec => {
      if (rec.targets.length === 0 && !rec.tripped) {
        rec.gaveUp = false;
        rec.firstTriedAt = this.now();
        maxWait = Math.max(maxWait, rec.waitForElementMs);
      }
    });
    this._flush();
    this._ensureGlobalObserver();
    if (maxWait > 0) this._scheduleMissingCheck(maxWait);
  }

  isApplied(experimentId) {
    return this.applied.has(String(experimentId));
  }

  status() {
    const applied = [];
    this.applied.forEach((rec, experimentId) => {
      applied.push({ experimentId, groupId: rec.groupId, appliedAt: rec.appliedAt, changes: rec.keys.length });
    });
    const pending = [];
    const tripped = [];
    this.domChanges.forEach(rec => {
      if (rec.tripped) tripped.push(rec.key);
      else if (rec.targets.length === 0) pending.push(rec.key);
    });
    return { applied, pending, tripped, cssRules: this.cssRules.size };
  }

  /**
   * Status of one change of an applied experiment: 'applied', 'waiting'
   * (no element yet, still within waitForElementMs), 'missing', 'conflict'
   * (circuit breaker tripped) or 'disabled'. Null when the experiment is not
   * applied at all.
   */
  changeStatus(experimentId, change) {
    if (!change) return 'missing';
    if (change.enabled === false) return 'disabled';
    const expId = String(experimentId);
    const record = this.applied.get(expId);
    if (!record) return null;
    const key = `${expId}::${change.id}`;
    const rec = this.domChanges.get(key);
    if (rec) {
      if (rec.tripped) return 'conflict';
      if (rec.targets.some(t => t.el.isConnected)) return 'applied';
      return rec.gaveUp || this.now() - rec.firstTriedAt >= rec.waitForElementMs ? 'missing' : 'waiting';
    }
    // CSS-only change: its rule exists when the change was usable at all.
    if (!this.cssRules.has(key)) return 'missing';
    if (this._query(change.selector).length > 0) return 'applied';
    return this.now() - record.appliedAt >= record.waitForElementMs ? 'missing' : 'waiting';
  }

  destroy() {
    this.revertAll();
    this._stopGlobalObserver();
    this.timers.forEach(t => clearTimeout(t));
    this.timers.clear();
    if (this.styleEl && this.styleEl.parentNode) this.styleEl.parentNode.removeChild(this.styleEl);
    this.styleEl = null;
    if (this.sheet && this.doc && this.doc.adoptedStyleSheets) {
      try { this.doc.adoptedStyleSheets = this.doc.adoptedStyleSheets.filter(s => s !== this.sheet); } catch (e) { /* ignore */ }
    }
    this.sheet = null;
  }

  // ── css ───────────────────────────────────────────────────────────────

  _validSelector(selector) {
    try { this.doc.querySelector(selector); return true; } catch (e) { return false; }
  }

  /** Managed-stylesheet parts of an insert: its own css and, for replace, the hide rule. */
  _insertCss(key, change) {
    if (change.css != null && change.css !== '') {
      const css = sanitizeCss(change.css);
      if (css) this.cssRules.set(`${key}::css`, css);
      else this.logger.warn('[B2M Visual] insert css rejected:', change.id);
    }
    if (change.position === 'replace') this.cssRules.set(`${key}::replace`, replaceRule(key));
  }

  _firstMatchIsImg(selector) {
    const el = this._query(selector)[0];
    return !el || String(el.tagName).toUpperCase() === 'IMG';
  }

  _renderCss() {
    const text = Array.from(this.cssRules.values()).join('\n');
    if (!text && !this.sheet && !this.styleEl) return;
    const doc = this.doc;
    if (!this.sheet && !this.styleEl) {
      const win = this.win;
      if (doc && 'adoptedStyleSheets' in doc && win && typeof win.CSSStyleSheet === 'function') {
        try {
          const sheet = new win.CSSStyleSheet();
          if (typeof sheet.replaceSync === 'function') {
            doc.adoptedStyleSheets = [...doc.adoptedStyleSheets, sheet];
            this.sheet = sheet;
          }
        } catch (e) { this.sheet = null; }
      }
      if (!this.sheet) {
        const el = doc.createElement('style');
        el.setAttribute(STYLE_ATTR, '');
        if (this.nonce) el.setAttribute('nonce', this.nonce);
        (doc.head || doc.documentElement).appendChild(el);
        this.styleEl = el;
      }
    }
    if (this.sheet) {
      try { this.sheet.replaceSync(text); } catch (e) { this.logger.warn('[B2M Visual] stylesheet update failed:', e); }
    } else if (this.styleEl) {
      if (this.styleEl.textContent !== text) this.styleEl.textContent = text;
      if (!this.styleEl.isConnected) (doc.head || doc.documentElement).appendChild(this.styleEl);
    }
  }

  // ── dom ───────────────────────────────────────────────────────────────

  _query(selector) {
    let list;
    try { list = Array.from(this.doc.querySelectorAll(selector)); } catch (e) { return []; }
    // Never target markup we inserted ourselves: an `insert` whose selector
    // also matches its own HTML would otherwise grow on every pass.
    list = list.filter(el => !(el.closest && el.closest(`[${INSERTED_ATTR}]`)));
    return this.exclude ? list.filter(el => !this.exclude(el)) : list;
  }

  /** Match the selector and apply to new elements. Returns the target count. */
  _resolveTargets(rec) {
    if (rec.tripped) return rec.targets.length;
    // Drop elements the framework threw away.
    const before = rec.targets.length;
    rec.targets = rec.targets.filter(t => {
      if (t.el.isConnected) return true;
      // The anchor element is gone: take its inserted HTML with it.
      (t.inserted || []).forEach(n => { if (n.parentNode) n.parentNode.removeChild(n); });
      return false;
    });
    const dropped = before - rec.targets.length;
    // A replaced element counts as a re-apply: a framework that re-creates
    // the node on every render must trip the breaker like one that rewrites
    // the value in place.
    if (dropped > 0 && !this._countReapply(rec)) return rec.targets.length;
    const known = new Set(rec.targets.map(t => t.el));
    let matches = this._query(rec.change.selector);
    if (!rec.change.matchAll) matches = matches.slice(0, 1);
    matches.forEach(el => {
      if (known.has(el)) return;
      if (!rec.change.matchAll && rec.targets.length > 0) return;
      const target = { el, original: this._captureOriginal(rec.change, el), observer: null, inserted: [] };
      rec.targets.push(target);
      this._write(rec, target);
      this._observe(rec, target);
    });
    return rec.targets.length;
  }

  _captureOriginal(change, el) {
    switch (change.action) {
      case 'text':
        return { text: el.textContent };
      case 'attribute':
        return { has: el.hasAttribute(change.name), value: el.getAttribute(change.name) };
      case 'image': {
        const original = {
          src: el.getAttribute('src'), srcset: el.getAttribute('srcset'), sizes: el.getAttribute('sizes'),
          alt: el.getAttribute('alt'), sources: []
        };
        const picture = el.parentElement && String(el.parentElement.tagName).toUpperCase() === 'PICTURE' ? el.parentElement : null;
        if (picture) {
          Array.from(picture.querySelectorAll('source')).forEach(s => {
            original.sources.push({ el: s, srcset: s.getAttribute('srcset'), type: s.getAttribute('type') });
          });
        }
        return original;
      }
      default:
        return {};
    }
  }

  _desired(rec, el) {
    const { change } = rec;
    switch (change.action) {
      case 'text':
        return el.textContent === change.value;
      case 'attribute':
        return change.value == null ? !el.hasAttribute(change.name) : el.getAttribute(change.name) === change.value;
      case 'image':
        return el.getAttribute('src') === change.value.src && !el.hasAttribute('srcset');
      case 'insert': {
        const t = rec.targets.find(x => x.el === el);
        if (!t || !t.inserted.length || !t.inserted.every(n => n.isConnected)) return false;
        return change.position !== 'replace' || this._hasReplaceToken(el, rec.key);
      }
      default:
        return true;
    }
  }

  _insert(rec, target) {
    const { change } = rec;
    const el = target.el;
    target.inserted.forEach(n => { if (n.parentNode) n.parentNode.removeChild(n); });
    target.inserted = [];
    const position = INSERT_POSITIONS.includes(change.position) ? change.position : 'afterend';
    const parent = el.parentNode;
    if ((position === 'beforebegin' || position === 'afterend' || position === 'replace') && !parent) return;
    const fragment = sanitizeHtml(this.doc, change.value);
    if (!fragment) return;
    const nodes = Array.from(fragment.childNodes);
    const marked = [];
    nodes.forEach(n => {
      if (n.nodeType === 1) { n.setAttribute(INSERTED_ATTR, rec.key); marked.push(n); }
      else if (n.nodeType === 3 && n.textContent.trim()) {
        // Wrap loose text so it can be tracked and removed again.
        const span = this.doc.createElement('span');
        span.setAttribute(INSERTED_ATTR, rec.key);
        fragment.replaceChild(span, n);
        span.appendChild(n);
        marked.push(span);
      } else {
        fragment.removeChild(n);
      }
    });
    const imported = this.doc.importNode(fragment, true);
    target.inserted = Array.from(imported.childNodes);
    if (position === 'beforebegin') parent.insertBefore(imported, el);
    else if (position === 'afterend' || position === 'replace') parent.insertBefore(imported, el.nextSibling);
    else if (position === 'afterbegin') el.insertBefore(imported, el.firstChild);
    else el.appendChild(imported);
    // replace: the element itself is hidden by the managed stylesheet rule
    // that matches this marker.
    if (position === 'replace') this._setReplaceToken(el, rec.key, true);
  }

  _hasReplaceToken(el, key) {
    const list = String(el.getAttribute(REPLACED_ATTR) || '').split(/\s+/);
    return list.includes(replaceToken(key));
  }

  _setReplaceToken(el, key, on) {
    const token = replaceToken(key);
    const list = String(el.getAttribute(REPLACED_ATTR) || '').split(/\s+/).filter(t => t && t !== token);
    if (on) list.push(token);
    if (list.length) el.setAttribute(REPLACED_ATTR, list.join(' '));
    else el.removeAttribute(REPLACED_ATTR);
  }

  _write(rec, target) {
    const { change } = rec;
    const el = target.el;
    if (this._desired(rec, el)) return;
    switch (change.action) {
      case 'text':
        el.textContent = change.value;
        break;
      case 'attribute':
        if (!ATTR_ALLOWLIST.includes(change.name)) return;
        if (change.value == null) el.removeAttribute(change.name);
        else el.setAttribute(change.name, change.value);
        break;
      case 'image':
        el.setAttribute('src', change.value.src);
        el.removeAttribute('srcset');
        el.removeAttribute('sizes');
        if (change.value.alt != null) el.setAttribute('alt', change.value.alt);
        (target.original.sources || []).forEach(s => {
          if (s.el && s.el.isConnected) s.el.setAttribute('srcset', change.value.src);
        });
        break;
      case 'insert':
        this._insert(rec, target);
        break;
      default:
        break;
    }
  }

  _restoreTarget(rec, target) {
    if (target.observer) { try { target.observer.disconnect(); } catch (e) { /* ignore */ } target.observer = null; }
    if (rec.change.action === 'insert') {
      (target.inserted || []).forEach(n => { if (n.parentNode) n.parentNode.removeChild(n); });
      target.inserted = [];
      if (rec.change.position === 'replace' && target.el) {
        try { this._setReplaceToken(target.el, rec.key, false); } catch (e) { /* ignore */ }
      }
      return;
    }
    const el = target.el;
    if (!el || !el.isConnected) return;
    const o = target.original || {};
    try {
      switch (rec.change.action) {
        case 'text':
          if (el.textContent !== o.text) el.textContent = o.text;
          break;
        case 'attribute':
          if (o.has) el.setAttribute(rec.change.name, o.value); else el.removeAttribute(rec.change.name);
          break;
        case 'image':
          if (o.src != null) el.setAttribute('src', o.src); else el.removeAttribute('src');
          if (o.srcset != null) el.setAttribute('srcset', o.srcset); else el.removeAttribute('srcset');
          if (o.sizes != null) el.setAttribute('sizes', o.sizes); else el.removeAttribute('sizes');
          if (o.alt != null) el.setAttribute('alt', o.alt); else el.removeAttribute('alt');
          (o.sources || []).forEach(s => {
            if (!s.el) return;
            if (s.srcset != null) s.el.setAttribute('srcset', s.srcset); else s.el.removeAttribute('srcset');
          });
          break;
        default:
          break;
      }
    } catch (e) {
      this.logger.warn('[B2M Visual] restore failed:', e);
    }
  }

  _observerOptions(change) {
    switch (change.action) {
      case 'text':
        return { childList: true, characterData: true, subtree: true };
      case 'attribute':
        return { attributes: true, attributeFilter: [change.name] };
      case 'image':
        return { attributes: true, attributeFilter: ['src', 'srcset', 'sizes'] };
      case 'insert':
        return { childList: true };
      default:
        return null;
    }
  }

  _observe(rec, target) {
    const win = this.win;
    const opts = this._observerOptions(rec.change);
    if (!opts || !win || typeof win.MutationObserver !== 'function') return;
    try {
      const observer = new win.MutationObserver(() => {
        if (rec.tripped || !target.el.isConnected) return;
        if (this._desired(rec, target.el)) return;
        if (!this._countReapply(rec)) return;
        this._write(rec, target);
      });
      // An insert next to the element lives in its parent: watch that list.
      const outside = rec.change.action === 'insert' && rec.change.position !== 'afterbegin' && rec.change.position !== 'beforeend';
      const node = outside && target.el.parentNode ? target.el.parentNode : target.el;
      observer.observe(node, opts);
      // replace: also put the hide marker back when the page drops it.
      if (rec.change.action === 'insert' && rec.change.position === 'replace') {
        observer.observe(target.el, { attributes: true, attributeFilter: [REPLACED_ATTR] });
      }
      target.observer = observer;
    } catch (e) {
      this.logger.warn('[B2M Visual] observer failed:', e);
    }
  }

  /** Sliding-window counter; trips the breaker and returns false when exceeded. */
  _countReapply(rec) {
    const now = this.now();
    rec.reapplies.push(now);
    while (rec.reapplies.length && now - rec.reapplies[0] > 1000) rec.reapplies.shift();
    if (rec.reapplies.length > MAX_REAPPLIES_PER_SECOND) {
      this._trip(rec);
      return false;
    }
    return true;
  }

  _trip(rec) {
    if (rec.tripped) return;
    rec.tripped = true;
    rec.targets.forEach(t => {
      if (t.observer) { try { t.observer.disconnect(); } catch (e) { /* ignore */ } t.observer = null; }
    });
    this.logger.warn('[B2M Visual] change disabled after re-apply storm:', rec.key);
    this._safeTrack('ve_change_conflict', {
      experiment_id: rec.expId, change_id: rec.change.id, action: rec.change.action, selector: rec.change.selector
    });
  }

  _safeTrack(name, params) {
    try { this.track(name, params); } catch (e) { /* telemetry must never break the page */ }
  }

  // ── global observer / scheduling ──────────────────────────────────────

  _ensureGlobalObserver() {
    if (this.globalObserver || !this.win || typeof this.win.MutationObserver !== 'function') return;
    if (this.domChanges.size === 0) return;
    const root = this.doc.documentElement || this.doc;
    try {
      this.globalObserver = new this.win.MutationObserver(() => this._schedule());
      this.globalObserver.observe(root, { childList: true, subtree: true });
    } catch (e) {
      this.globalObserver = null;
    }
  }

  _stopGlobalObserver() {
    if (this.globalObserver) { try { this.globalObserver.disconnect(); } catch (e) { /* ignore */ } }
    this.globalObserver = null;
  }

  _schedule() {
    if (this.flushScheduled) return;
    this.flushScheduled = true;
    const run = () => { this.flushScheduled = false; this._flush(); };
    if (this.win && typeof this.win.requestAnimationFrame === 'function') this.win.requestAnimationFrame(run);
    else setTimeout(run, 0);
  }

  _flush() {
    this.domChanges.forEach(rec => {
      if (rec.tripped || rec.gaveUp) return;
      try { this._resolveTargets(rec); } catch (e) { /* ignore */ }
    });
  }

  _scheduleMissingCheck(waitMs) {
    const t = setTimeout(() => {
      this.timers.delete(t);
      this._flush();
      this.domChanges.forEach(rec => {
        if (rec.tripped || rec.missingReported || rec.targets.length > 0) return;
        if (this.now() - rec.firstTriedAt < rec.waitForElementMs) return;
        rec.missingReported = true;
        rec.gaveUp = true;
        this._safeTrack('ve_change_missing', {
          experiment_id: rec.expId, change_id: rec.change.id, action: rec.change.action, selector: rec.change.selector
        });
      });
    }, Math.max(0, waitMs) + 20);
    this.timers.add(t);
  }

  _pendingCount(expId) {
    let n = 0;
    this.domChanges.forEach(rec => { if (rec.expId === expId && rec.targets.length === 0 && !rec.tripped) n++; });
    return n;
  }
}
